/* eslint-disable max-lines */
import { NonAuTermsModal } from '@/components/chat/non-au-terms-modal';
import { communityJsonSource } from '@/container/community/communityJsonSource';
import { communityXmlSource } from '@/container/community/communityXmlSource';
import { useIsJsonFormEnabled, useIsXmlFormEnabled } from '@/features/flags';
import { useQuerySpecialtyTranscript } from '@/hooks/query/useQuerySpecialtyTranscript';
import { type DetailedPublicTemplateResponse } from '@/types/global-types';
import {
  Banner,
  BannerContent,
  BannerDescription,
  BannerTitle,
} from '@heidi/design-system/banner';
import { Button } from '@heidi/design-system/button';
import { FileCard } from '@heidi/design-system/file-card';
import { Textarea } from '@heidi/design-system/textarea';
import {
  TypographyCaption,
  TypographyFootNote,
  TypographyH5,
} from '@heidi/design-system/typography';
import { Loader2, RotateCcw, Sparkles, Upload } from 'lucide-react';
import { useCallback, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  AUDIO_ACCEPT,
  AUDIO_ALLOWED_MIME,
  DOCUMENT_ACCEPT,
  DOCUMENT_ALLOWED_MIME,
  TRANSCRIPT_MAX_CHARS,
} from './constants';
import { formFillDemoMessages } from './template-form-demo.messages';
import { type FormDemoSubmitSource, type XmlFormFillResult } from './types';
import { useFillFormSubmit } from './use-template-form-demo-fill';
import {
  useTemplateFormDemoUpload,
  type UploadFillSource,
} from './use-template-form-demo-upload';

type DemoState = 'idle' | 'loading' | 'filled' | 'error';
type PendingUnauthedAction = 'upload' | null;
const COMBINED_UPLOAD_ACCEPT = `${AUDIO_ACCEPT},${DOCUMENT_ACCEPT}`;
const AUDIO_EXTENSIONS = new Set(['mp3', 'wav', 'm4a', 'webm', 'ogg']);
const DOCUMENT_EXTENSIONS = new Set(['pdf', 'doc', 'docx']);
const UNAUTHED_TERMS_GATE_CHAR_COUNT = 20;

const getFileExtension = (fileName: string): string =>
  fileName.split('.').pop()?.toLowerCase() ?? '';

const getUploadSource = (file: File): UploadFillSource | null => {
  const mime = (file.type || '').toLowerCase();
  const extension = getFileExtension(file.name);

  if (
    AUDIO_ALLOWED_MIME.has(mime) ||
    mime.startsWith('audio/') ||
    AUDIO_EXTENSIONS.has(extension)
  ) {
    return 'audio';
  }

  if (DOCUMENT_ALLOWED_MIME.has(mime) || DOCUMENT_EXTENSIONS.has(extension)) {
    return 'document';
  }

  return null;
};

const getSelectedUploadSource = (
  selectedAudioFile: File | null,
  selectedDocumentFile: File | null
): UploadFillSource | null => {
  if (selectedAudioFile) return 'audio';
  if (selectedDocumentFile) return 'document';
  return null;
};

const getSelectedUploadSuccessMessage = (source: UploadFillSource | null) =>
  source === 'audio'
    ? formFillDemoMessages.uploadSuccessAudio
    : formFillDemoMessages.uploadSuccessDocument;

interface FormFillDemoProps {
  template: DetailedPublicTemplateResponse;
  /**
   * Called when a successful fill produces a previewable URL (data/object URL)
   * for the filled PDF. The parent (`details.tsx`) swaps its `<PdfViewer>` source.
   */
  onFilledPdfReady: (url: string) => void;
  /**
   * Called when an XML form fill returns field values. PDF fills use
   * `onFilledPdfReady` instead.
   */
  onXmlFilled?: (result: XmlFormFillResult) => void;
  /**
   * Called when a JSON form fill returns field values.
   */
  onJsonFilled?: (fieldValues: Record<string, string>) => void;
  /**
   * Called when the user resets the demo, so the parent restores the original
   * empty-template preview URL.
   */
  onReset: () => void;
  /**
   * Shows a post-fill signup CTA in the success state.
   */
  showSignupCta?: boolean;
  /**
   * Called when the user clicks the post-fill signup CTA.
   */
  onSignupCtaClick?: () => void;
  /**
   * Controls unauthenticated one-time terms gating for upload and typing interactions.
   */
  isUnauthed?: boolean;
  /**
   * Tracks how the user attempted to fill the form.
   */
  onSubmitAttempt?: (source: FormDemoSubmitSource) => void;
  /**
   * Tracks when the user loads the sample transcript into the textarea.
   */
  onTrySampleClick?: () => void;
}

export const TemplateFormDemo = ({
  template,
  onFilledPdfReady,
  onXmlFilled,
  onJsonFilled,
  onReset,
  showSignupCta = false,
  onSignupCtaClick,
  isUnauthed = false,
  onSubmitAttempt,
  onTrySampleClick,
}: FormFillDemoProps) => {
  const intl = useIntl();
  const isXmlFormEnabled = useIsXmlFormEnabled();
  const isJsonFormEnabled = useIsJsonFormEnabled();
  const [transcript, setTranscript] = useState('');
  const [demoState, setDemoState] = useState<DemoState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedAudioFile, setSelectedAudioFile] = useState<File | null>(null);
  const [selectedDocumentFile, setSelectedDocumentFile] = useState<File | null>(null);
  const [hasAcceptedUnauthedTerms, setHasAcceptedUnauthedTerms] = useState(false);
  const [isUnauthedTermsOpen, setIsUnauthedTermsOpen] = useState(false);
  const [pendingUnauthedAction, setPendingUnauthedAction] =
    useState<PendingUnauthedAction>(null);
  const [isTranscriptFromSample, setIsTranscriptFromSample] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  const formFields = useMemo(
    () => template.template_content.form_fields ?? [],
    [template.template_content.form_fields]
  );
  const s3ObjectKey = template.template_content.s3_object_key;
  const xmlSource = communityXmlSource(template, isXmlFormEnabled);
  const jsonSource = communityJsonSource(template, isJsonFormEnabled);

  const { isFetching: isSampleLoading, refetch: refetchSpecialtyTranscript } =
    useQuerySpecialtyTranscript(template.specialty, intl.locale);

  const { isPending: isTextFillPending, submit: submitFillForm } = useFillFormSubmit({
    template,
    onSubmit: () => {
      setDemoState('loading');
      setErrorMessage(null);
    },
    onSucceeded: (filledPdfUrl) => {
      setDemoState('filled');
      onFilledPdfReady(filledPdfUrl);
    },
    onXmlSucceeded: (result) => {
      setDemoState('filled');
      onXmlFilled?.(result);
    },
    onJsonSucceeded: (fieldValues) => {
      setDemoState('filled');
      onJsonFilled?.(fieldValues);
    },
    onFailed: (message) => {
      setErrorMessage(message);
      setDemoState('error');
    },
  });

  const clearSelectedUploads = useCallback(() => {
    setSelectedAudioFile(null);
    setSelectedDocumentFile(null);
  }, []);

  const handleUploadSuccess = useCallback(
    (file: File, source: UploadFillSource) => {
      clearSelectedUploads();
      if (source === 'audio') {
        setSelectedAudioFile(file);
      } else {
        setSelectedDocumentFile(file);
      }
      setTranscript('');
      setIsTranscriptFromSample(false);
      setDemoState('idle');
      setErrorMessage(null);
    },
    [clearSelectedUploads]
  );

  const handleUploadError = useCallback((message: string) => {
    setErrorMessage(message);
  }, []);

  const handleUploadStart = useCallback(() => {
    setErrorMessage(null);
  }, []);

  const {
    handleAudioFile,
    handleDocumentFiles,
    isPending: isUploadFillPending,
    submit: submitFillFromUpload,
  } = useTemplateFormDemoUpload({
    template,
    onSubmit: () => {
      setDemoState('loading');
      setErrorMessage(null);
    },
    onSucceeded: (filledPdfUrl) => {
      setDemoState('filled');
      onFilledPdfReady(filledPdfUrl);
    },
    onXmlSucceeded: (result) => {
      setDemoState('filled');
      onXmlFilled?.(result);
    },
    onJsonSucceeded: (fieldValues) => {
      setDemoState('filled');
      onJsonFilled?.(fieldValues);
    },
    onFailed: (message) => {
      setErrorMessage(message);
      setDemoState('error');
    },
    onFileReady: handleUploadSuccess,
    onError: handleUploadError,
    onStart: handleUploadStart,
  });

  const isAnyPending = isTextFillPending || isUploadFillPending;

  const trimmedTranscript = transcript.trim();
  const isTranscriptValid =
    trimmedTranscript.length > 0 && trimmedTranscript.length <= TRANSCRIPT_MAX_CHARS;

  const handleSampleClick = useCallback(async () => {
    if (!template.specialty || isAnyPending) return;

    onTrySampleClick?.();

    setErrorMessage(null);
    const sampleResponse = await refetchSpecialtyTranscript();
    const sampleTranscript = sampleResponse.data?.transcript;
    if (!sampleTranscript) return;

    clearSelectedUploads();
    setTranscript(sampleTranscript);
    setIsTranscriptFromSample(true);
    setDemoState('idle');
  }, [
    clearSelectedUploads,
    isAnyPending,
    onTrySampleClick,
    refetchSpecialtyTranscript,
    template.specialty,
  ]);

  const handleTranscriptChange = useCallback(
    (nextTranscript: string) => {
      const wasTranscriptFromSample = isTranscriptFromSample;
      const crossedTypingThreshold =
        transcript.length < UNAUTHED_TERMS_GATE_CHAR_COUNT &&
        nextTranscript.length >= UNAUTHED_TERMS_GATE_CHAR_COUNT;
      const shouldOpenUnauthedTermsModal =
        isUnauthed &&
        !hasAcceptedUnauthedTerms &&
        ((wasTranscriptFromSample &&
          nextTranscript.length >= UNAUTHED_TERMS_GATE_CHAR_COUNT) ||
          (!wasTranscriptFromSample && crossedTypingThreshold));

      clearSelectedUploads();
      setTranscript(nextTranscript);
      setIsTranscriptFromSample(false);
      setDemoState('idle');
      setErrorMessage(null);
      if (shouldOpenUnauthedTermsModal) {
        setPendingUnauthedAction(null);
        setIsUnauthedTermsOpen(true);
      }
    },
    [
      clearSelectedUploads,
      hasAcceptedUnauthedTerms,
      isTranscriptFromSample,
      isUnauthed,
      transcript.length,
    ]
  );

  const handleSubmit = useCallback(() => {
    if (selectedAudioFile) {
      onSubmitAttempt?.('upload_audio');
      submitFillFromUpload({ source: 'audio', file: selectedAudioFile });
      return;
    }

    if (selectedDocumentFile) {
      onSubmitAttempt?.('upload_document');
      submitFillFromUpload({ source: 'document', file: selectedDocumentFile });
      return;
    }

    if (!isTranscriptValid) return;
    onSubmitAttempt?.(isTranscriptFromSample ? 'sample' : 'manual_transcript');
    submitFillForm(transcript);
  }, [
    isTranscriptFromSample,
    isTranscriptValid,
    onSubmitAttempt,
    selectedAudioFile,
    selectedDocumentFile,
    submitFillForm,
    submitFillFromUpload,
    transcript,
  ]);

  const handleReset = useCallback(() => {
    setTranscript('');
    setIsTranscriptFromSample(false);
    setDemoState('idle');
    setErrorMessage(null);
    clearSelectedUploads();
    onReset();
  }, [clearSelectedUploads, onReset]);

  const handleDocumentFile = useCallback(
    (file: File) => {
      handleDocumentFiles([file]);
    },
    [handleDocumentFiles]
  );

  const handleClearAudioFile = useCallback(() => {
    setSelectedAudioFile(null);
    setErrorMessage(null);
  }, []);

  const handleClearDocumentFile = useCallback(() => {
    setSelectedDocumentFile(null);
    setErrorMessage(null);
  }, []);

  const handleClearSelectedFile = useCallback(() => {
    if (selectedAudioFile) {
      handleClearAudioFile();
      return;
    }

    if (selectedDocumentFile) {
      handleClearDocumentFile();
    }
  }, [
    handleClearAudioFile,
    handleClearDocumentFile,
    selectedAudioFile,
    selectedDocumentFile,
  ]);

  const handleUploadButtonClick = useCallback(() => {
    if (isAnyPending) return;
    if (isUnauthed && !hasAcceptedUnauthedTerms) {
      setPendingUnauthedAction('upload');
      setIsUnauthedTermsOpen(true);
      return;
    }
    uploadInputRef.current?.click();
  }, [hasAcceptedUnauthedTerms, isAnyPending, isUnauthed]);

  const handleUnauthedTermsProceed = useCallback(() => {
    setHasAcceptedUnauthedTerms(true);
    setIsUnauthedTermsOpen(false);

    if (pendingUnauthedAction === 'upload' && !isAnyPending) {
      uploadInputRef.current?.click();
    }

    setPendingUnauthedAction(null);
  }, [isAnyPending, pendingUnauthedAction]);

  const handleUploadInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;

      const source = getUploadSource(file);
      if (source === 'audio') {
        handleAudioFile(file);
        return;
      }

      if (source === 'document') {
        handleDocumentFile(file);
        return;
      }

      setErrorMessage(intl.formatMessage(formFillDemoMessages.errorDocumentInvalid));
    },
    [handleAudioFile, handleDocumentFile, intl]
  );

  if ((!s3ObjectKey && !xmlSource && !jsonSource) || formFields.length === 0) {
    return null;
  }

  const isFilledState = demoState === 'filled';
  // An XML or JSON fill renders a field list, not a PDF, so the PDF wording
  // would describe something the user cannot see. The PDF copy keeps its own
  // message so its existing translations survive.
  const isStructuredForm = Boolean(xmlSource || jsonSource);
  const selectedUploadSource = getSelectedUploadSource(
    selectedAudioFile,
    selectedDocumentFile
  );
  const selectedUploadFile = selectedAudioFile ?? selectedDocumentFile;
  const isSubmitEnabled = Boolean(selectedUploadFile) || isTranscriptValid;
  const selectedUploadSuccessMessage =
    getSelectedUploadSuccessMessage(selectedUploadSource);
  const selectedFileCardClassName = isAnyPending
    ? 'h-auto w-full max-w-80 p-2 opacity-60'
    : 'h-auto w-full max-w-80 p-2';
  const selectedUploadFileType = selectedUploadFile?.name.includes('.')
    ? selectedUploadFile.name.split('.').pop()?.toUpperCase() || 'FILE'
    : 'FILE';

  return (
    <>
      <section
        id="form-fill-demo-section"
        data-testid="form-fill-demo"
        className="flex size-full flex-col gap-6 rounded-lg border border-border bg-surface p-4"
      >
        <header className="flex flex-col gap-8">
          <div className="flex justify-between gap-2 sm:items-center">
            <TypographyH5>
              <FormattedMessage {...formFillDemoMessages.sectionHeading} />
            </TypographyH5>
            {!isFilledState && (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="xxs"
                  onClick={() => void handleSampleClick()}
                  disabled={isAnyPending || isSampleLoading || !template.specialty}
                  disableAutomaticSvgSizing
                  className="flex w-full items-center gap-2 sm:w-auto"
                  data-testid="form-fill-demo-sample-button"
                >
                  {isSampleLoading ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <Sparkles size={12} />
                  )}
                  <FormattedMessage {...formFillDemoMessages.trySampleButton} />
                </Button>
                <Button
                  variant="outline"
                  size="xxs"
                  onClick={handleUploadButtonClick}
                  disabled={isAnyPending}
                  disableAutomaticSvgSizing
                  className="flex w-full items-center gap-2 sm:w-auto"
                  data-testid="form-fill-demo-upload-button"
                >
                  <Upload size={12} />
                  <FormattedMessage {...formFillDemoMessages.uploadFileButton} />
                </Button>
                <input
                  ref={uploadInputRef}
                  type="file"
                  className="hidden"
                  accept={COMBINED_UPLOAD_ACCEPT}
                  onChange={handleUploadInputChange}
                  data-testid="form-fill-demo-upload-input"
                />
              </div>
            )}
          </div>
          <TypographyCaption className="text-text-secondary">
            <FormattedMessage {...formFillDemoMessages.sectionDescriptionWithUploads} />
          </TypographyCaption>
        </header>

        {isFilledState ? (
          <div className="flex flex-col gap-4">
            <Banner subVariant="success">
              <BannerContent>
                <BannerTitle>
                  <FormattedMessage
                    {...(isStructuredForm
                      ? formFillDemoMessages.filledStructuredFormReadyHeading
                      : formFillDemoMessages.filledPdfReadyHeading)}
                  />
                </BannerTitle>
                <BannerDescription>
                  <FormattedMessage {...formFillDemoMessages.filledPdfReadyDescription} />
                </BannerDescription>
              </BannerContent>
            </Banner>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="lg"
                variant="outline"
                onClick={handleReset}
                disableAutomaticSvgSizing
                className="flex w-full items-center gap-2"
              >
                <RotateCcw size={12} />
                <FormattedMessage {...formFillDemoMessages.tryAnotherButton} />
              </Button>
              {showSignupCta && (
                <Button
                  size="lg"
                  onClick={onSignupCtaClick}
                  data-testid="form-fill-demo-signup-button"
                  className="w-full"
                >
                  <FormattedMessage
                    {...formFillDemoMessages.signUpToSaveFilledFormButton}
                  />
                </Button>
              )}
            </div>
          </div>
        ) : (
          <>
            {selectedUploadFile && selectedUploadSource ? (
              <div className="flex h-full items-center rounded-lg border border-border bg-surface p-3">
                <div className="flex w-full flex-col items-center justify-center gap-2">
                  <FileCard
                    data-testid="form-fill-demo-selected-file"
                    fileName={selectedUploadFile.name}
                    fileType={selectedUploadFileType}
                    onDelete={isAnyPending ? undefined : () => handleClearSelectedFile()}
                    className={selectedFileCardClassName}
                  />
                  <TypographyFootNote className="text-text-tertiary">
                    <FormattedMessage {...selectedUploadSuccessMessage} />
                  </TypographyFootNote>
                </div>
              </div>
            ) : (
              <div className="flex flex-1 flex-col gap-2">
                <label htmlFor="form-fill-demo-transcript" className="sr-only">
                  <FormattedMessage {...formFillDemoMessages.transcriptLabel} />
                </label>
                <Textarea
                  id="form-fill-demo-transcript"
                  data-testid="form-fill-demo-transcript"
                  wrapperClassName="h-full"
                  className="h-full"
                  value={transcript}
                  maxLength={TRANSCRIPT_MAX_CHARS}
                  placeholder={intl.formatMessage(
                    formFillDemoMessages.transcriptPlaceholder
                  )}
                  onChange={(event) => handleTranscriptChange(event.target.value)}
                  disabled={isAnyPending}
                />
                <TypographyCaption className="text-right text-text-tertiary">
                  <FormattedMessage
                    {...formFillDemoMessages.charCount}
                    values={{
                      count: transcript.length,
                      max: TRANSCRIPT_MAX_CHARS,
                    }}
                  />
                </TypographyCaption>
              </div>
            )}

            {errorMessage && (
              <Banner subVariant="error">
                <BannerContent>
                  <BannerTitle>
                    <FormattedMessage {...formFillDemoMessages.errorHeading} />
                  </BannerTitle>
                  <BannerDescription>{errorMessage}</BannerDescription>
                </BannerContent>
              </Banner>
            )}

            <div className="w-full">
              <Button
                size="sm"
                onClick={handleSubmit}
                disabled={!isSubmitEnabled || isAnyPending}
                disableAutomaticSvgSizing
                className="flex w-full items-center justify-center gap-2"
                data-testid="form-fill-demo-submit-button"
              >
                {isAnyPending ? (
                  <>
                    <Loader2 size={12} className="animate-spin" />
                    <FormattedMessage {...formFillDemoMessages.filling} />
                  </>
                ) : (
                  <FormattedMessage {...formFillDemoMessages.fillFormButton} />
                )}
              </Button>
            </div>
          </>
        )}
      </section>
      <NonAuTermsModal
        open={isUnauthedTermsOpen}
        onProceed={handleUnauthedTermsProceed}
      />
    </>
  );
};

export default TemplateFormDemo;
